import React, { useState, useEffect, useCallback, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import confetti from "canvas-confetti";
import { Header } from "./components/Header";
import { SettingsModal } from "./components/SettingsModal";
import { FloatingBubbles } from "./components/FloatingBubbles";
import { cancelSpeech, speakText, unlockSpeech } from "./bingo-shared/speech";
import { getColumnForNumber } from "./utils/bingoLogic";
import { playPopSound, playWhooshSound, playVictorySound } from "./utils/sound";

type Theme =
  | "bubblegum"
  | "neon-glow"
  | "tropical"
  | "candy"
  | "space"
  | "retro"
  | "halloween"
  | "christmas"
  | "valentine";

type HeaderColor = "gray" | "blue" | "red" | "dark";
type CalledBall = { letter: string; number: number };

const headerColorMap: Record<HeaderColor, string> = {
  gray: "#334155",
  blue: "#2563eb",
  red: "#dc2626",
  dark: "#18181b",
};

const App: React.FC = () => {
  const [theme, setTheme] = useState<Theme>(() => {
    const saved = localStorage.getItem("bingoCallerState");
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        if (parsed.theme) return parsed.theme as Theme;
      } catch {}
    }
    return "bubblegum";
  });

  const [called, setCalled] = useState<CalledBall[]>([]);
  const [isDrawing, setIsDrawing] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [hideCalled, setHideCalled] = useState(false);
  const [soundEnabled, setSoundEnabled] = useState(true);
  const [voiceRepeats, setVoiceRepeats] = useState(1);
  const [overlayFontSize, setOverlayFontSize] = useState(45);
  const [headerColor, setHeaderColor] = useState<HeaderColor>("dark");
  const [useSameColorForHighlight, setUseSameColorForHighlight] =
    useState(true);
  const [highlightColor, setHighlightColor] = useState<HeaderColor>("dark");
  const [floatingEmojiDensity, setFloatingEmojiDensity] = useState(12);

  const [flyingNumber, setFlyingNumber] = useState<CalledBall | null>(null);
  const [showBingoCelebration, setShowBingoCelebration] = useState(false);
  const drawTimerRef = useRef<number | null>(null);

  const DISPLAY_DURATION = 5000;

  useEffect(() => {
    const saved = localStorage.getItem("bingoCallerState");
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        if (parsed.called) setCalled(parsed.called);
        if (typeof parsed.hideCalled === "boolean")
          setHideCalled(parsed.hideCalled);
        if (parsed.overlayFontSize) setOverlayFontSize(parsed.overlayFontSize);
        if (parsed.headerColor) setHeaderColor(parsed.headerColor);
        if (typeof parsed.useSameColorForHighlight === "boolean")
          setUseSameColorForHighlight(parsed.useSameColorForHighlight);
        if (parsed.highlightColor) setHighlightColor(parsed.highlightColor);
        if (typeof parsed.soundEnabled === "boolean")
          setSoundEnabled(parsed.soundEnabled);
        if (parsed.voiceRepeats) setVoiceRepeats(parsed.voiceRepeats);
        if (parsed.floatingEmojiDensity)
          setFloatingEmojiDensity(parsed.floatingEmojiDensity);
      } catch {}
    }
  }, []);

  useEffect(() => {
    localStorage.setItem(
      "bingoCallerState",
      JSON.stringify({
        called,
        theme,
        hideCalled,
        overlayFontSize,
        headerColor,
        useSameColorForHighlight,
        highlightColor,
        soundEnabled,
        voiceRepeats,
        floatingEmojiDensity,
      }),
    );
  }, [
    called,
    theme,
    hideCalled,
    overlayFontSize,
    headerColor,
    useSameColorForHighlight,
    highlightColor,
    soundEnabled,
    voiceRepeats,
    floatingEmojiDensity,
  ]);

  useEffect(() => {
    document.body.setAttribute("data-theme", theme);
  }, [theme]);

  useEffect(() => {
    return () => {
      if (drawTimerRef.current != null) window.clearTimeout(drawTimerRef.current);
    };
  }, []);

  const drawBall = useCallback(() => {
    if (isDrawing || flyingNumber) return;
    const used = new Set(called.map((c) => c.number));
    const available = Array.from({ length: 75 }, (_, i) => i + 1).filter(
      (n) => !used.has(n),
    );
    if (available.length === 0) return;

    const num = available[Math.floor(Math.random() * available.length)];
    const letter = getColumnForNumber(num);
    const newBall = { letter, number: num };

    setIsDrawing(true);
    setFlyingNumber(newBall);
    unlockSpeech();
    if (soundEnabled) playWhooshSound();
    speakText(`${letter} ${num}`, voiceRepeats);

    if (drawTimerRef.current != null) window.clearTimeout(drawTimerRef.current);
    drawTimerRef.current = window.setTimeout(() => {
      drawTimerRef.current = null;
      setCalled((prev) =>
        prev.some((ball) => ball.number === newBall.number) ? prev : [...prev, newBall],
      );
      setFlyingNumber(null);
      setIsDrawing(false);
      if (soundEnabled) playPopSound();
    }, DISPLAY_DURATION);
  }, [called, isDrawing, flyingNumber, soundEnabled, voiceRepeats]);

  const resetGame = () => {
    if (drawTimerRef.current != null) {
      window.clearTimeout(drawTimerRef.current);
      drawTimerRef.current = null;
    }
    cancelSpeech();
    setCalled([]);
    setFlyingNumber(null);
    setIsDrawing(false);
  };

  const triggerBingoCelebration = () => {
    const count = 900;

    const fire = (particleRatio: number, opts: any) => {
      confetti({
        ...opts,
        particleCount: Math.floor(count * particleRatio),
        zIndex: 400,
      });
    };

    fire(0.35, { spread: 70, startVelocity: 65, origin: { y: 0.6 } });
    fire(0.3, { spread: 100, origin: { y: 0.65 } });
    fire(0.4, { spread: 130, decay: 0.9, scalar: 0.85, origin: { y: 0.7 } });
    fire(0.25, {
      spread: 180,
      startVelocity: 40,
      decay: 0.92,
      origin: { y: 0.65 },
    });

    setTimeout(() => {
      confetti({
        particleCount: 400,
        spread: 220,
        origin: { y: 0.55 },
        zIndex: 250,
      });
    }, 320);

    if (soundEnabled) playVictorySound();
    speakText("BINGO!", 2);

    setShowBingoCelebration(true);
    setTimeout(() => setShowBingoCelebration(false), 5200);
  };

  const handleSetTheme = (newTheme: string) => setTheme(newTheme as Theme);

  const calledCount = called.length;
  const headerBgColor = headerColorMap[headerColor];
  const highlightBgColor = useSameColorForHighlight
    ? headerColorMap[headerColor]
    : headerColorMap[highlightColor];

  return (
    <div className="bubbly-bg min-h-screen flex flex-col text-pink-950 overflow-hidden">
      <FloatingBubbles theme={theme} density={floatingEmojiDensity} />

      <div className="relative z-10 flex flex-col flex-1 max-w-[1400px] mx-auto w-full px-4">
        <Header onOpenSettings={() => setShowSettings(true)} />

        {/* Main content area that stretches between header and bottom */}
        <div className="flex flex-col flex-1 min-h-0">
          {/* DRAW NEXT button */}
          <div className="pt-3 pb-4 flex-shrink-0">
            <button
              onClick={drawBall}
              disabled={isDrawing || !!flyingNumber}
              className="bouncy-btn w-full py-4 text-2xl font-black rounded-3xl bg-gradient-to-r from-zinc-950 to-blue-700 text-white shadow-xl disabled:opacity-60"
            >
              DRAW NEXT
            </button>
          </div>

          {/* Bingo Grid - stretches to fill available space above bottom bar */}
          <div
            className="flex-1"
            style={{ display: "flex" }}
            onClick={drawBall}
          >
            <div
              className="bg-white/92 rounded-3xl p-4 shadow-xl flex-1"
              // style={{ height: "75vh" }}
            >
              <div className="grid grid-cols-5 gap-2 h-full">
                {["B", "I", "N", "G", "O"].map((col, colIndex) => {
                  const start = colIndex * 15 + 1;
                  const nums = Array.from({ length: 15 }, (_, i) => start + i);

                  return (
                    <div
                      key={col}
                      className="flex flex-col h-full"
                      style={{ containerType: "inline-size" }}
                    >
                      {/* Header */}
                      <div
                        className="text-center font-black text-5xl py-3 rounded-2xl text-white tracking-[6px] mb-2 flex-shrink-0"
                        style={{
                          backgroundColor: headerBgColor,
                          // fontSize: "3cqh",
                        }}
                      >
                        {col}
                      </div>

                      {/* Numbers */}
                      <div
                        className="flex-1 flex flex-col gap-1.5 min-h-0"
                        style={{ containerType: "inline-size" }}
                      >
                        {nums.map((num) => {
                          const isCalled = called.some((c) => c.number === num);
                          if (hideCalled && isCalled) return null;

                          return (
                            <div
                              key={num}
                              className={`flex-1 flex items-center justify-center rounded-2xl text-3xl md:text-[2rem] font-bold transition-all ${isCalled ? "text-white" : "bg-white border border-black-100 text-black-900"}`}
                              style={
                                isCalled
                                  ? {
                                      backgroundColor: highlightBgColor,
                                      fontSize: "3.5cqh",
                                    }
                                  : { fontSize: "4cqh" }
                              }
                            >
                              {num}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Bottom section */}
          <div className="pt-2 pb-3 flex-shrink-0">
            <div className="text-center mb-2">
              <span className="text-sm font-semibold text-pink-700">
                {calledCount} / 75 called
              </span>
            </div>
            <button
              onClick={triggerBingoCelebration}
              className="bouncy-btn w-full py-4 text-xl font-black rounded-3xl bg-gradient-to-r from-zinc-950 to-blue-700 text-white shadow-xl"
            >
              🎉 Mark as BINGO!
            </button>
          </div>
        </div>
      </div>

      {/* Flying Number Overlay */}
      <AnimatePresence>
        {flyingNumber && (
          <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/75 backdrop-blur-2xl p-4">
            <motion.div
              initial={{ scale: 0.4, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.6, opacity: 0 }}
              transition={{ type: "spring", stiffness: 100, damping: 20 }}
              className="text-center"
            >
              <div
                style={{
                  fontSize: `clamp(3rem, ${overlayFontSize}vw, 80vh)`,
                  maxWidth: "95vw",
                  lineHeight: 1,
                }}
                className="font-black text-white tabular-nums tracking-[-0.04em] drop-shadow-[0_25px_70px_rgba(0,0,0,0.6)]"
              >
                {flyingNumber.letter}
                {flyingNumber.number}
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* BINGO Celebration Overlay */}
      <AnimatePresence>
        {showBingoCelebration && (
          <div
            className="fixed inset-0 z-[300] flex items-center justify-center bg-black/85 backdrop-blur-md"
            onClick={() => setShowBingoCelebration(false)}
          >
            <motion.div
              className="text-center px-6 relative"
              style={{ position: "relative", zIndex: 10 }}
              animate={{ scale: [1, 1.12, 1] }}
              transition={{
                duration: 1.1,
                repeat: Infinity,
                ease: "easeInOut",
              }}
            >
              <div className="text-[22vw] md:text-[17vw] font-black text-white tracking-[-0.07em] drop-shadow-[0_20px_80px_rgba(0,0,0,0.7)]">
                BINGO!
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      <SettingsModal
        isOpen={showSettings}
        onClose={() => setShowSettings(false)}
        voiceRepeats={voiceRepeats}
        setVoiceRepeats={setVoiceRepeats}
        soundEnabled={soundEnabled}
        setSoundEnabled={setSoundEnabled}
        currentTheme={theme}
        setTheme={handleSetTheme}
        onResetGame={resetGame}
        autoMark={false}
        setAutoMark={() => {}}
        overlayFontSize={overlayFontSize}
        setOverlayFontSize={setOverlayFontSize}
        headerColor={headerColor}
        setHeaderColor={setHeaderColor}
        useSameColorForHighlight={useSameColorForHighlight}
        setUseSameColorForHighlight={setUseSameColorForHighlight}
        highlightColor={highlightColor}
        setHighlightColor={setHighlightColor}
        hideCalled={hideCalled}
        setHideCalled={setHideCalled}
        floatingEmojiDensity={floatingEmojiDensity}
        setFloatingEmojiDensity={setFloatingEmojiDensity}
      />
    </div>
  );
};

export default App;
